/**
 * Palette — search dialog.
 *
 * Behaviour (design.md §13a.4, Track 4.5.F scope-cut):
 *   - Desktop: 600px wide floating input centred horizontally, ~120px from top.
 *   - Mobile (<768px): full-screen sheet sliding from the top.
 *   - Opens via the labelled Search-button click which dispatches
 *     "proof:open-palette" CustomEvent (no keyboard shortcut at MVP — wishlisted
 *     at .plan/wishlist/keyboard-shortcuts-and-palette.md).
 *   - Fuzzy-matches against the locally-cached library snapshot (recipes,
 *     artefacts, batches). No command catalogue at MVP. Variations are not a
 *     palette kind (2026-07-10 versions-first cleanup).
 *   - Enter activates focused result; Esc dismisses.
 *   - Recent queries persist per-session (sessionStorage), NOT across reloads.
 *   - No server round-trip — all matching is client-side.
 *
 * Design refs:
 *   design.md §13a.4 (lines 1079–1096)
 *   design.md §2 — one accent, hairlines only, 200ms ease
 *   design.md §14.1 — breakpoints (<768px = mobile)
 *   tokens.css — design tokens
 *
 * Vocabulary (branding.md §5.2.1, §8):
 *   - "Attempt" / "Cook" not "commit"
 *   - No banned words in any user-facing string
 *   - No emoji, no exclamations
 *   - aria-label = "Search" (not "Command palette")
 *
 * Keyboard navigation:
 *   - Arrow up/down: move selection in result list
 *   - Enter: activate selected result
 *   - Esc: close dialog
 *
 * ARIA: combobox pattern. The input is role="combobox", the list is
 * role="listbox", each row is role="option". aria-activedescendant points to
 * the selected row.
 *
 * Rule 7 (CLAUDE.md): mobile + tablet + desktop from the first commit.
 *   375px+ all behaviours tested in *.test.tsx.
 */

import { useState, useEffect, useRef, useCallback, useMemo, type CSSProperties } from "react";
import type { BackendApi, SearchResultItem } from "@proof/client-core/backend";
import {
  getCommands,
  type PaletteResult,
  type RecipePaletteResult,
  type ArtefactPaletteResult,
  type BatchPaletteResult,
  type LibrarySnapshot,
} from "@proof/client-core/palette";
import type { Recipe } from "../explorer/types.js";
import { ResultRow } from "./ResultRow.js";

// ── Server-search tuning ──────────────────────────────────────────────────────

/**
 * Debounce window before firing the server full-text query. Local title/slug
 * results render immediately on every keystroke; the server (FTS5/BM25) call is
 * debounced so we don't issue a round-trip per character. 180ms is inside the
 * 150–200ms target band and keeps the dialog responsive.
 */
const SERVER_SEARCH_DEBOUNCE_MS = 180;

/**
 * Settle window before warming the highlighted recipe's open-path data
 * (PERF-W1 c). Arrow-key scrolling moves the selection row-by-row; without a
 * debounce every intermediate row would fire a prefetch. 150ms lets a fast
 * scroll pass through a row without warming it, and fires once the cursor
 * rests on a result — the same intent signal as a sustained card-hover
 * (FlatRecipeList uses 120ms). Each uuid is warmed at most once per palette
 * session (see prefetchedUuidsRef).
 */
const PALETTE_PREFETCH_DEBOUNCE_MS = 150;

/**
 * Tables the palette searches server-side.
 *
 * Scope-cut (adversarial review 2026-06-14): only "recipe" at MVP because the
 * activation handlers in Workspace.tsx / app.tsx have no-op `default: break`
 * for artefact/batch rows — surfacing those would be fake wiring (CLAUDE.md
 * Rule: no fake wiring in the actual app). Artefact/batch activatable search
 * is a post-MVP item.
 */
const SERVER_SEARCH_TABLES = ["recipe"] as const;

// ── Snapshot-load tuning ───────────────────────────────────────────────────────

/**
 * Page size for the library snapshot list calls (recipe.list / artefact.list /
 * batch.list).
 *
 * This MUST stay ≤ the server input cap. The list procedures validate
 * `limit: z.number().int().min(1).max(100)` (apps/api/src/routes/recipes/index.ts,
 * batches/index.ts, artefacts/index.ts). A client value above that cap is a hard
 * 400 — which, before this constant existed, silently emptied the whole snapshot
 * and killed local title/slug matching (the "search returns nothing for pizza"
 * P0). The named constant is the regression guard: a client/server page-size
 * mismatch can no longer ship as a magic number buried in three call sites.
 */
const SNAPSHOT_PAGE_SIZE = 100;

/**
 * Upper bound on items accumulated per kind while paginating the snapshot. One
 * page of 100 caps a large library, so loadSnapshot follows `nextCursor` until
 * exhausted OR this cap. Bounded so the prefetch can never fan out unboundedly
 * on a 10k-recipe library — local matching over the first 500 is more than
 * enough; the server FTS path covers the long tail.
 */
const SNAPSHOT_MAX_ITEMS = 500;

/**
 * Kinds that are allowed in the merged palette results list. Only recipe is
 * surfaced — artefact + batch activation handlers are no-ops in Workspace.tsx /
 * app.tsx at MVP (adversarial review 2026-06-14 fix #1), and variations were
 * removed from the UI (2026-07-10 versions-first cleanup; the kebab escape
 * hatch is the sole legacy variation surface).
 */
const ALLOWED_PALETTE_KINDS: ReadonlySet<string> = new Set(["recipe"]);

/**
 * Map one server FTS5 result (`SearchResultItem`) into the `PaletteResult` shape
 * `ResultRow` renders. At MVP we search only recipes server-side, so the server
 * always returns `kind: "recipe"`. The default branch guards against unexpected
 * server payload shapes.
 *
 * matchIndices is empty: FTS5 matches the body, not the title, so there are no
 * title char-offsets to highlight. ResultRow renders the plain label when
 * indices are empty.
 *
 * `snippet` is always null at MVP; reserved for Phase 2 body-match highlighting.
 */
function mapServerResult(
  item: SearchResultItem,
): RecipePaletteResult | ArtefactPaletteResult | BatchPaletteResult | null {
  const base = {
    uuid: item.uuid,
    label: item.title,
    // snippet is always null at MVP (server returns null; Phase 2 will populate it).
    secondary: item.snippet,
    score: item.score,
    matchIndices: [] as number[],
  };
  switch (item.kind) {
    case "recipe":
      return { kind: "recipe", ...base };
    case "artefact":
      return { kind: "artefact", ...base };
    case "batch":
      return { kind: "batch", ...base };
    default:
      // Unknown kind from server — drop it rather than produce a broken row.
      return null;
  }
}

// ── Session-storage key ───────────────────────────────────────────────────────

const SESSION_KEY = "proof:palette-recent-queries";
const MAX_RECENT = 8;

function loadRecentQueries(): string[] {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

function saveRecentQuery(query: string): void {
  try {
    const prev = loadRecentQueries().filter((q) => q !== query);
    const next = [query, ...prev].slice(0, MAX_RECENT);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
    // eslint-disable-next-line no-restricted-syntax -- best-effort: recent-query history is a UX convenience, not user data; unavailable sessionStorage skips silently (Rule 8 documented degradation).
  } catch {
    // sessionStorage unavailable — silently skip
  }
}

// ── CSS ───────────────────────────────────────────────────────────────────────

const PALETTE_CSS = `
  /* Backdrop */
  .proof-palette-backdrop {
    position: fixed;
    inset: 0;
    z-index: 9000;
    background: rgba(10, 8, 6, 0.35);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 120px;
    /* 200ms ease per design tokens */
    animation: proof-palette-fade-in 200ms ease both;
  }

  @keyframes proof-palette-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  /* Panel — desktop */
  .proof-palette-panel {
    width: 600px;
    max-width: calc(100vw - 32px);
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-card);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 160px);
  }

  /* Panel — mobile full-screen sheet */
  @media (max-width: 767px) {
    .proof-palette-backdrop {
      padding-top: 0;
      align-items: stretch;
    }

    .proof-palette-panel {
      width: 100%;
      max-width: 100%;
      border-radius: 0 0 var(--radius-card) var(--radius-card);
      border-top: none;
      border-left: none;
      border-right: none;
      max-height: 80vh;
      /* Slide down from top */
      animation: proof-palette-slide-in 200ms ease both;
    }
  }

  @keyframes proof-palette-slide-in {
    from { transform: translateY(-100%); opacity: 0; }
    to   { transform: translateY(0);     opacity: 1; }
  }

  /* Input row */
  .proof-palette-input-row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: 0 var(--space-4);
    border-bottom: 1px solid var(--line);
    height: 48px;
    flex-shrink: 0;
  }

  .proof-palette-search-icon {
    font-family: var(--font-mono);
    font-size: 13px;
    color: var(--muted);
    flex-shrink: 0;
    user-select: none;
  }

  .proof-palette-input {
    flex: 1 1 0;
    border: none;
    background: transparent;
    font-family: var(--font-sans);
    font-size: 15px;
    color: var(--text);
    outline: none;
    min-width: 0;
  }

  .proof-palette-input::placeholder {
    color: var(--muted);
    font-size: 14px;
  }

  .proof-palette-dismiss-btn {
    flex-shrink: 0;
    background: none;
    border: 1px solid var(--line);
    border-radius: var(--radius-button);
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--muted);
    padding: 0 var(--space-2);
    height: 24px;
    cursor: pointer;
    transition: color var(--motion-state), border-color var(--motion-state);
    white-space: nowrap;
    -webkit-tap-highlight-color: transparent;
  }

  .proof-palette-dismiss-btn:hover {
    color: var(--text);
    border-color: color-mix(in srgb, var(--text) 20%, var(--line));
  }

  /* Result list */
  .proof-palette-list {
    list-style: none;
    overflow-y: auto;
    overscroll-behavior: contain;
    /*
     * flex-basis auto (not 0): the panel is a column flex container with only a
     * max-height (no explicit height), so a 0 flex-basis contributes nothing to
     * the panel's intrinsic height and the list collapses to 0px — rows exist
     * but are clipped invisible. auto lets the list size to its content while
     * min-height 0 + overflow-y auto keep it shrinking + scrolling once the
     * content exceeds the panel's max-height.
     */
    flex: 1 1 auto;
    min-height: 0;
  }

  /* Empty state */
  .proof-palette-empty {
    padding: var(--space-6) var(--space-4);
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--muted);
    text-align: center;
  }

  /* Recent queries header */
  .proof-palette-section-label {
    padding: var(--space-2) var(--space-4) var(--space-1);
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted);
    border-bottom: 1px solid var(--line);
  }

  .proof-palette-recent-btn {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--space-2) var(--space-4);
    background: none;
    border: none;
    border-bottom: 1px solid var(--line);
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--muted);
    cursor: pointer;
    text-align: left;
    transition: background var(--motion-state);
    min-height: 36px;
    -webkit-tap-highlight-color: transparent;
  }

  .proof-palette-recent-btn:last-child {
    border-bottom: none;
  }

  .proof-palette-recent-btn:hover {
    background: color-mix(in srgb, var(--text) 4%, transparent);
  }
`;

let paletteCssInjected = false;
function injectPaletteCss() {
  if (paletteCssInjected) return;
  if (typeof document === "undefined") return;
  const id = "proof-palette-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = PALETTE_CSS;
    document.head.appendChild(el);
  }
  paletteCssInjected = true;
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface PaletteProps {
  /** The backend API used to build the library snapshot. */
  api: BackendApi;
  /**
   * Called when the user activates a result. The palette closes itself.
   * Callers may navigate or perform the command.
   */
  onActivate?: (result: PaletteResult) => void;
  /**
   * In-memory recipe list for fallback filtering (Bug #7 fix).
   * When the API stub returns no results (demo mode), these are filtered by
   * case-insensitive substring on title and merged into the results.
   */
  recipes?: Recipe[];
  /**
   * PERF-W1 (c) — warm the open-path data for the currently-highlighted recipe
   * result. Fired debounced on selection-change (arrow keys / hover), at most
   * once per uuid per palette session, so opening the result is instant. The
   * parent supplies the SAME prefetch entry point the library card-hover uses
   * (app.tsx `handlePrefetchRecipe`); `warmBody: true` warms the collapsed
   * attempt.list open-path key. Best-effort — a rejection has no user-visible
   * impact (the activation's foreground fetch surfaces any real error).
   */
  onPrefetchRecipe?: (recipeUuid: string, opts?: { warmBody?: boolean }) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Palette({
  api,
  onActivate,
  recipes: recipesFallback,
  onPrefetchRecipe,
}: PaletteProps) {
  injectPaletteCss();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PaletteResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapshot, setSnapshot] = useState<LibrarySnapshot | null>(null);
  const [recentQueries, setRecentQueries] = useState<string[]>([]);
  // Server full-text (body) search results, merged into the result list when
  // they arrive. Keyed off the same query; cleared on close + on empty query.
  const [serverResults, setServerResults] = useState<PaletteResult[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Tracks the query that was in effect when the current selection was set.
  // When the query changes we reset to row 0; when only serverResults change
  // (arriving ~180ms after the keystroke) we clamp rather than reset so the
  // user's ArrowDown navigation is not cancelled by the server response landing.
  const prevQueryRef = useRef("");
  // Monotonic request id — guards against out-of-order server responses. The
  // oRPC client does not thread an AbortSignal, so we drop any response whose id
  // is not the latest issued (a stale keystroke's in-flight result is ignored).
  const serverReqIdRef = useRef(0);
  // PERF-W1 (c) — recipe uuids already warmed this palette session. Reset on
  // every open so re-opening the dialog re-warms (fresh 15s SWR window), but a
  // single session never fires more than one prefetch per uuid no matter how
  // often the cursor revisits the row.
  const prefetchedUuidsRef = useRef<Set<string>>(new Set());

  // ── Load library snapshot when palette opens ────────────────────────────────

  const loadSnapshot = useCallback(async () => {
    /**
     * Drain a keyset-paginated list endpoint by following `nextCursor` until it
     * is null OR we hit SNAPSHOT_MAX_ITEMS. Each page requests SNAPSHOT_PAGE_SIZE
     * (≤ the server cap), so a single page covers a small library and the cursor
     * loop covers a large one. Bounded by SNAPSHOT_MAX_ITEMS so the prefetch can
     * never fan out unboundedly.
     */
    async function drain<T>(
      page: (cursor: string | null) => Promise<{ items: T[]; nextCursor: string | null }>,
    ): Promise<T[]> {
      const acc: T[] = [];
      let cursor: string | null = null;
      // Hard page ceiling derived from the two bounds — defensive against a
      // server that never returns a null cursor.
      const maxPages = Math.ceil(SNAPSHOT_MAX_ITEMS / SNAPSHOT_PAGE_SIZE) + 1;
      for (let i = 0; i < maxPages; i++) {
        const { items, nextCursor } = await page(cursor);
        acc.push(...items);
        if (nextCursor === null || acc.length >= SNAPSHOT_MAX_ITEMS) break;
        cursor = nextCursor;
      }
      return acc.slice(0, SNAPSHOT_MAX_ITEMS);
    }

    try {
      // Only recipe results can surface in the palette
      // (ALLOWED_PALETTE_KINDS). artefact.list and batch.list were fetched here
      // and paginated (up to 6 round-trips each) but every artefact/batch hit
      // getCommands produced was dropped by the ALLOWED_PALETTE_KINDS filter
      // below — the fetches were pure wasted round-trips, and this codebase no
      // longer has a palette surface that opens an artefact or a batch. Snapshot
      // still carries empty artefacts/batches arrays so the LibrarySnapshot
      // shape (and getCommands' seam) is unchanged.
      const recipes = await drain(async (cursor) => {
        const res = await api.recipe.list({ limit: SNAPSHOT_PAGE_SIZE, cursor });
        return { items: res.recipes, nextCursor: res.nextCursor };
      });

      // Variations are not a palette kind (2026-07-10 versions-first cleanup),
      // so the snapshot no longer fans out an api.branch.list call per recipe —
      // that removes an N× network round-trip on every palette open.
      setSnapshot({
        recipes,
        artefacts: [],
        batches: [],
      });
    } catch (err) {
      // Non-fatal — palette degrades to commands-only if data load fails. This
      // is a background prefetch, not a user mutate, so per CLAUDE.md Rule 8 a
      // logged, named diagnostic is the correct floor (no user-facing toast for
      // a silent prefetch). The named event lets the e2e assert the snapshot
      // loaded cleanly — if a client/server page-size mismatch ever returns a
      // 400 again, this fires and the regression spec fails.
      console.error({ event: "palette_snapshot_load_failed", err });
      setSnapshot({
        recipes: [],
        artefacts: [],
        batches: [],
      });
    }
  }, [api]);

  // ── Open / close ────────────────────────────────────────────────────────────

  const openPalette = useCallback(() => {
    setOpen(true);
    setQuery("");
    setSelectedIndex(0);
    prevQueryRef.current = "";
    // PERF-W1 (c) — fresh prefetch budget per session (the SWR window a warm
    // opened last time has likely lapsed).
    prefetchedUuidsRef.current = new Set();
    setRecentQueries(loadRecentQueries());
  }, []);

  const closePalette = useCallback(() => {
    setOpen(false);
    setQuery("");
    setResults([]);
    setServerResults([]);
    setSelectedIndex(0);
    // Invalidate any in-flight server response so it cannot land after close.
    serverReqIdRef.current += 1;
  }, []);

  // ── Listen for "proof:open-palette" event ──────────────────────────────────
  //
  // Keyboard: no app-defined shortcuts at MVP. Per
  // .plan/features/2026-04-29-design-amendments.md §3.3, zero app-defined
  // single-key shortcuts; modifier-prefixed ⌘K binding is wishlisted at
  // .plan/wishlist/keyboard-shortcuts-and-palette.md. The palette opens via
  // labelled Search-button click only at MVP.

  useEffect(() => {
    function handleOpenEvent() {
      openPalette();
    }
    window.addEventListener("proof:open-palette", handleOpenEvent);
    return () => {
      window.removeEventListener("proof:open-palette", handleOpenEvent);
    };
  }, [openPalette]);

  // ── Focus input when opened ─────────────────────────────────────────────────

  useEffect(() => {
    if (open) {
      // Defer focus so the panel is rendered before we focus.
      const id = requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      void loadSnapshot();
      return () => cancelAnimationFrame(id);
    }
  }, [open, loadSnapshot]);

  // ── In-memory recipe fallback results (Bug #7 fix + Bug #186 fix) ──────────
  // Filter the recipesFallback prop (app.tsx in-memory recipe list) by
  // case-insensitive substring on title and convert to RecipePaletteResult shape
  // so ResultRow can render them.
  //
  // Bug #186: use substring match (`.includes`) — case-insensitive, tolerant of
  // leading/trailing whitespace. The fzf scorer also matches substrings, but a
  // recipe that was JUST CREATED may not appear in `snapshot` yet because the
  // SWR cache (staleTime 30s) hasn't been revalidated. This fallback ensures
  // in-memory recipes always participate in the results, regardless of cache state.
  const fallbackResults = useMemo<RecipePaletteResult[]>(() => {
    if (!recipesFallback || recipesFallback.length === 0) return [];
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return recipesFallback
      .filter((r) => r.title.toLowerCase().includes(q))
      .map((r) => ({
        kind: "recipe" as const,
        uuid: r.uuid,
        label: r.title,
        secondary: null,
        score: 1,
        matchIndices: [] as number[],
      }));
  }, [query, recipesFallback]);

  // ── Server full-text (body) search ──────────────────────────────────────────
  //
  // The local snapshot matches recipe/variation/artefact/batch by title/slug
  // only. To also find recipes by a word in their BODY (an ingredient, a step),
  // we call the per-user FTS5 backend (api.search.query). Results are debounced,
  // request-id-guarded against out-of-order responses, and MERGED into the local
  // results (server-first, deduped) by the recompute effect below.
  //
  // This is a READ, not a mutation. On failure we silently degrade to local
  // results — but we console.debug the error so the failure leaves a diagnostic
  // (CLAUDE.md Rule 8 governs mutates; we still never swallow without a trace).

  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length === 0) {
      // Empty query — drop any server results and skip the round-trip.
      setServerResults([]);
      serverReqIdRef.current += 1;
      return;
    }

    const reqId = (serverReqIdRef.current += 1);
    const timer = setTimeout(() => {
      void api.search
        .query({ q, tables: [...SERVER_SEARCH_TABLES], limit: 30 })
        .then((res) => {
          // Drop stale responses — a newer keystroke has superseded this one.
          if (reqId !== serverReqIdRef.current) return;
          // Shape guard: a malformed payload (no `results` array) degrades to
          // empty server results rather than throwing. Local/fallback results
          // still render — the catch below is the safety net; this is the
          // explicit, non-throwing path for a well-formed-but-empty response.
          const items: SearchResultItem[] = Array.isArray(res?.results) ? res.results : [];
          setServerResults(
            items
              .map(mapServerResult)
              .filter(
                (r): r is RecipePaletteResult | ArtefactPaletteResult | BatchPaletteResult =>
                  r !== null,
              ),
          );
        })
        .catch((err: unknown) => {
          if (reqId !== serverReqIdRef.current) return;
          // Silent degrade to local results — leave a diagnostic (Rule 8 spirit).
          console.debug("[palette] server search failed; using local results", err);
          setServerResults([]);
        });
    }, SERVER_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, open, api]);

  // ── Recompute results on query / snapshot change ────────────────────────────

  useEffect(() => {
    if (!open) {
      setResults([]);
      return;
    }
    // Treat a null snapshot as empty for the LOCAL pass. The snapshot loader
    // (loadSnapshot) does recipe.list before setSnapshot, so `snapshot` is
    // null for a real window on dev. Previously this effect wiped ALL results
    // (server + local + fallback) to [] during that window, so a server match
    // for "pizza" that had already resolved was discarded and the dialog showed
    // "No results". By falling through with an empty local snapshot, the
    // server + in-memory fallback merge runs immediately and renders while the
    // snapshot is still loading.
    const snap: LibrarySnapshot = snapshot ?? {
      recipes: [],
      artefacts: [],
      batches: [],
    };
    const apiResults = getCommands(query, snap, 20);
    // Merge in-memory fallback recipes that are NOT already in the API snapshot.
    // This covers two cases:
    //   1. Demo / stub backend (snapshot.recipes.length === 0) — original Bug #7 fix.
    //   2. Bug #186: a recipe was JUST CREATED. The SWR cache may still hold a
    //      stale snapshot from before the create, so `apiResults` won't contain
    //      the new recipe. The app.tsx `recipes` state is always kept up-to-date
    //      (setRecipes fires synchronously in handleCreateRecipe) so merging the
    //      fallback here ensures the new recipe is always findable immediately.
    const snapshotUuids = new Set(snap.recipes.map((r) => r.uuid));
    const extraFallback = fallbackResults.filter(
      (fb) =>
        !snapshotUuids.has(fb.uuid) &&
        !apiResults.some((r) => (r as RecipePaletteResult).uuid === fb.uuid),
    );

    // Local-only matches (title/slug snapshot + in-memory fallback). These come
    // AFTER server (BM25-ranked, body-aware) results in the merged list.
    const localResults: PaletteResult[] = [...extraFallback, ...apiResults];

    // Merge: server full-text results FIRST (body-aware, BM25-ranked), then any
    // local match not already present. Dedupe by uuid; cap at 20 (design.md
    // §13a.4 keeps the list short). serverResults is empty until the debounced
    // call resolves, so local results render immediately on every keystroke and
    // server results merge in when they arrive — no UI jank while pending.
    const seen = new Set<string>();
    const merged: PaletteResult[] = [];
    for (const r of [...serverResults, ...localResults]) {
      const uuid = (r as { uuid: string }).uuid;
      if (seen.has(uuid)) continue;
      seen.add(uuid);
      // Drop artefact + batch kinds — their activation handlers are no-ops at MVP
      // (adversarial review 2026-06-14 fix #1: no fake wiring).
      if (!ALLOWED_PALETTE_KINDS.has(r.kind)) continue;
      merged.push(r);
      if (merged.length >= 20) break;
    }

    // Keyboard-nav cursor management (adversarial review 2026-06-14 fix #2):
    // - When the QUERY changes → reset to row 0 (new search intent).
    // - When only SERVER RESULTS change (arriving ~180ms later) → clamp the
    //   existing index so ArrowDown navigation made before the server responds
    //   is not wiped out.
    const queryChanged = query !== prevQueryRef.current;
    prevQueryRef.current = query;
    setResults(merged);
    if (queryChanged) {
      setSelectedIndex(0);
    } else {
      setSelectedIndex((prev) => Math.min(prev, Math.max(merged.length - 1, 0)));
    }
  }, [query, snapshot, open, fallbackResults, serverResults]);

  // ── Scroll selected row into view ───────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    const selected = list.querySelector('[aria-selected="true"]') as HTMLElement | null;
    if (selected && typeof selected.scrollIntoView === "function") {
      selected.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex, open]);

  // ── Prefetch the highlighted recipe's open-path data (PERF-W1 c) ────────────
  //
  // Palette switches previously got zero prefetch, so activating a result paid
  // the full cold open-path round-trip. Warm the highlighted recipe's collapsed
  // attempt.list key on selection-change so opening it is instant. Debounced so
  // arrow-key scrolling through a long list does not fire a prefetch per row —
  // only a rested cursor (or a hover, which also sets selectedIndex) warms. Each
  // uuid is warmed at most once per palette session (prefetchedUuidsRef).
  //
  // Best-effort read: onPrefetchRecipe is itself fire-and-forget (its own catch
  // logs at console.debug), so there is no user-facing error path here.
  useEffect(() => {
    if (!open) return;
    const prefetch = onPrefetchRecipe;
    if (!prefetch) return;
    const selected = results[selectedIndex];
    if (!selected || selected.kind !== "recipe") return;
    const uuid = (selected as { uuid: string }).uuid;
    if (prefetchedUuidsRef.current.has(uuid)) return;

    const timer = setTimeout(() => {
      // Re-check inside the timer: a concurrent fire (or a re-run that resolved
      // first) may have already claimed this uuid.
      if (prefetchedUuidsRef.current.has(uuid)) return;
      prefetchedUuidsRef.current.add(uuid);
      prefetch(uuid, { warmBody: true });
    }, PALETTE_PREFETCH_DEBOUNCE_MS);

    // A scroll that moves off this row before the settle window cancels the
    // timer — the uuid is NOT added to the set, so it stays eligible if the
    // cursor returns and rests.
    return () => clearTimeout(timer);
  }, [open, results, selectedIndex, onPrefetchRecipe]);

  // ── Keyboard navigation ─────────────────────────────────────────────────────

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      closePalette();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const result = results[selectedIndex];
      if (result) {
        handleActivate(result);
      }
      return;
    }
  }

  // ── Activate a result ───────────────────────────────────────────────────────

  function handleActivate(result: PaletteResult) {
    if (query.trim().length > 0) {
      saveRecentQuery(query.trim());
    }
    closePalette();
    onActivate?.(result);
  }

  // ── Recent query click ──────────────────────────────────────────────────────

  function handleRecentClick(q: string) {
    setQuery(q);
    inputRef.current?.focus();
  }

  // ── Backdrop click ──────────────────────────────────────────────────────────

  function handleBackdropClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) {
      closePalette();
    }
  }

  // ── Active descendant id ────────────────────────────────────────────────────

  const activeDescendantId =
    results.length > 0 ? `proof-palette-result-${selectedIndex}` : undefined;

  // ── Render ──────────────────────────────────────────────────────────────────

  if (!open) return null;

  const showRecent = query.trim().length === 0 && recentQueries.length > 0;
  const showEmpty = query.trim().length > 0 && results.length === 0;

  return (
    <div
      className="proof-palette-backdrop"
      role="presentation"
      onClick={handleBackdropClick}
      data-testid="palette-backdrop"
    >
      <div
        className="proof-palette-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        /*
         * 2026-05-09 P1 fix — opt the search palette out of the capsule's
         * modal-detection minimise. The capsule auto-minimises when any
         * `[role=dialog][aria-modal=true]` is mounted (see
         * use-capsule-minimise.ts Bug 3a) to avoid overlapping confirmation-
         * modal footer buttons. The Palette is a centred search overlay; it
         * does not conflict with the capsule and must NOT cause it to drop
         * the slot labels + the `+` button.
         */
        data-capsule-passthrough="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input row */}
        <div className="proof-palette-input-row">
          <span className="proof-palette-search-icon" aria-hidden="true">
            /
          </span>
          <input
            ref={inputRef}
            className="proof-palette-input"
            type="text"
            role="combobox"
            aria-label="Search recipes"
            aria-expanded={results.length > 0}
            aria-controls="proof-palette-listbox"
            aria-activedescendant={activeDescendantId}
            aria-autocomplete="list"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="Search recipes…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
          />
          <button
            type="button"
            className="proof-palette-dismiss-btn"
            onClick={closePalette}
            aria-label="Close search"
          >
            Esc
          </button>
        </div>

        {/* Recent queries (shown when query is empty) */}
        {showRecent && (
          <>
            <div className="proof-palette-section-label" aria-hidden="true">
              Recent
            </div>
            {recentQueries.map((q) => (
              <button
                key={q}
                type="button"
                className="proof-palette-recent-btn"
                onClick={() => handleRecentClick(q)}
                aria-label={`Recent search: ${q}`}
              >
                {q}
              </button>
            ))}
          </>
        )}

        {/* Results */}
        {results.length > 0 && (
          <ul
            ref={listRef}
            id="proof-palette-listbox"
            className="proof-palette-list"
            role="listbox"
            aria-label="Results"
          >
            {results.map((result, i) => (
              <ResultRow
                key={`${result.kind}-${(result as { uuid: string }).uuid}`}
                result={result}
                selected={i === selectedIndex}
                index={i}
                onActivate={handleActivate}
                onHover={() => setSelectedIndex(i)}
              />
            ))}
          </ul>
        )}

        {/* Empty state */}
        {showEmpty && (
          <div className="proof-palette-empty" role="status">
            No results for "{query}"
          </div>
        )}
      </div>
    </div>
  );
}

// ── Unused style placeholder (removes lint warning) ───────────────────────────
const _unusedStyle: CSSProperties = {};
void _unusedStyle;
